import React, { useState, useEffect } from "react";
import CardViz from "./CardViz";


const PROFILE = {
  name: "Sumer S. Vaid",
  title: "AI Research Scientist, Builder & Computational Behavioral Scientist",
  location: "Boston, MA",
  bio: [
    "Sumer S. Vaid is an AI research scientist, builder and computational behavioral scientist working as a postdoctoral fellow at the AI Institute at Harvard Business School, combining machine learning with empirical social science to build and evaluate AI systems.",
    "Current work spans post-training, multi-agent systems, and mechanistic interpretability. Projects include post-training open-weight models on human behavioral data and benchmarks that test whether simulated people behave like real people. Other studies ask how large groups of AI agents organize, in simulated firms and in swarms of up to 1,000 agents. MOSAIC, an open-source laboratory, connects computations inside individual agents to behavior across organizations of 50 to 5,000 agents. A paper in preparation develops a measurement-theoretic framework for mechanistic interpretability. Open-source tools for building and evaluating AI agents include Everval and Reflex.",
    "Earlier papers modeled social media habits with transformer and LSTM networks, and measured how personality and situational context shape everyday smartphone use. Others tested generative AI for personalized persuasion at scale and recognized human emotion from footstep-induced floor vibrations.",
    "The research draws on computer science, psychology and management practice. Publications appear in IEEE Transactions on Affective Computing, ACM IMWUT, Nature Reviews Psychology, the Journal of Personality and Social Psychology and Harvard Business Review. Awards include the Best Dissertation Award from the Psychology of Technology Institute. Grant funders include the Digital Data Design Institute at Harvard and the Stanford Institute for Human-Centered AI.",
  ],
  email: "svaid@hbs.edu",
  links: {
    github: "https://github.com/sumervaid",
    linkedin: "https://www.linkedin.com/in/sumer-vaid-ph-d-93ba1522/",
    scholar: "https://scholar.google.com/citations?user=vaamhuwAAAAJ&hl=en",
    cv: null, // set to "/<file>.pdf" once a current CV PDF is placed in public/
  },
  skills: [
    "System Design", "Research Design", "Python", "TypeScript", "PyTorch", "JAX", "Transformers", "QLoRA / SFT",
    "LLM Agents", "Multi-Agent Systems", "Reinforcement Learning", "RL Environments", "Evals & Benchmarks",
    "MCP", "Next.js", "GCP", "Causal Inference", "Psychometrics",
  ],
};

// Systems and engineering work. Every number below traces to the project's own
// README, results CSV or load-test report (see WEBSITE_SOURCES.md).
const SYSTEMS = [
  {
    title: "PsyDyBench",
    status: "v1.0.0",
    viz: "psydybench",
    area: ["evals"],
    blurb: "PsyDyBench is a psychometric benchmark for multi-day LLM personas, covering 300 personas over 6 simulated days and 36 models from 9 developers ranging from 1B to 671B parameters. Models get the direction of known psychological effects right 84.3% of the time. Effect sizes remain far too small, with a within-person |r| of 0.023 against a human range of 0.20 to 0.70. Larger models score about as low as smaller models.",
    stack: ["Benchmarking", "36 LLMs", "Psychometrics"],
    links: [
      { label: "arXiv", href: "https://arxiv.org/abs/2610.04246" },
      { label: "GitHub", href: "https://github.com/sumervaid/PsyDyBench" },
    ],
  },
  {
    title: "MOSAIC",
    status: "v0.1.1",
    viz: "mosaic",
    area: ["agents", "simulation", "platform"],
    blurb: "MOSAIC (Multilevel Observation of Swarms with Agent Interpretability and Control) is an open-source laboratory for organizational science with AI agents. MOSAIC connects computations inside individual agents to behavior across organizations of 50 to 5,000 agents. Researchers trace how an organizational choice changes agents' internal representations, how those changes spread through communication, and how the changes shape collective performance. Researchers can pause an experiment, branch from a shared starting point, and change an internal state, a message, or an organizational rule. A first demonstration asks agent teams to develop, verify, and combine intermediate mathematical results under different divisions of labor and authority. The project includes the Behavioral Science Agent Arena, a live Qualtrics-style platform for replicated multi-agent conversations with humans in the loop. Researchers define conditions, build digital twins from Big Five, HEXACO and Schwartz-value profiles, and export the data. A stress test of 60 concurrent rooms finished with zero failures.",
    stack: ["Open-weight models", "Mechanistic interpretability", "Multi-agent systems", "Next.js", "SQLite", "GCP"],
    links: [{ label: "GitHub", href: "https://github.com/sumervaid/mosaic-atlas" }],
  },
  {
    title: "Everval",
    status: "v0.3.1",
    viz: "everval",
    area: ["evals", "tooling"],
    blurb: "Everval provides open-source, on-premises continuous benchmarking for AI models and agents. Everval turns repository history, documents and workflow simulations into frozen benchmarks, then compares systems on quality, cost and latency as models, prompts and code change. A repository task counts only when the tests fail before the original fix and pass after the fix. A local terminal workbench schedules re-checks and enforces spending limits. Everval collects zero telemetry.",
    stack: ["Python", "TypeScript", "Terminal UI", "Docker", "tmux"],
    links: [
      { label: "GitHub", href: "https://github.com/sumervaid/everval-ai" },
      { label: "npm", href: "https://www.npmjs.com/package/everval-ai" },
    ],
  },
  {
    title: "Generative Organizations",
    status: "In development",
    viz: "genorg",
    area: ["agents", "simulation"],
    blurb: "Generative Organizations couples persona models with world models to simulate whole firms. The generative-agent loop of memory, planning and reflection extends from individuals to an organization of several hundred agents in a seeded social network. More than 100 simulations have produced about 48,000 agent-days. A companion simulator tests AI-tool rollout strategies on employee digital twins.",
    stack: ["Generative agents", "Social networks", "Streamlit", "Cloud Run"],
    links: [],
  },
  {
    title: "Post-training models on human dynamics",
    status: "In development",
    viz: "posttrain",
    area: ["post-training"],
    blurb: "This project fine-tunes open-weight base models to predict how a person's psychological states change from day to day. Training to date uses QLoRA supervised fine-tuning of Qwen3-4B-Base on 67,555 examples from 2,042 participants in public experience-sampling studies. The plan scales to 70B-class base models, with a public checkpoint once data-use review is complete.",
    stack: ["Qwen3", "QLoRA", "SFT", "Modal"],
    links: [],
  },
  {
    title: "Scaling AI Workforces",
    status: "Pilot",
    viz: "scaling",
    area: ["agents", "evals"],
    blurb: "This project studies how large groups of AI agents should organize. Pilot studies tested swarms of up to 1,000 agents on a coordination task, along with 126 swarms of theorem-proving agents that produced 46,595 Lean-verified submissions. Pilot results to date are null or negative. These results shape the design of the main study.",
    stack: ["Python", "Lean", "GPT-5 nano"],
    links: [],
  },
  {
    title: "Reflex",
    status: "v0.5.2",
    viz: "reflex",
    area: ["agents", "tooling"],
    blurb: "Reflex is an open-source CLI that reads Claude Code, Codex and Cursor session history, finds workflows repeated across sessions, and turns those workflows into agent skills and memory. A model router picks a model for each new task by matching the task to the most similar past sessions. Randomized exploration and a head-to-head bench test whether a cheaper model would have done as well, with the bench scoring one task on each model against the user's own test command. Session analysis stays on the local machine.",
    stack: ["TypeScript", "SQLite", "ONNX embeddings", "Model routing", "Claude Code"],
    links: [
      { label: "GitHub", href: "https://github.com/sumervaid/reflex-tui" },
      { label: "npm", href: "https://www.npmjs.com/package/reflex-skills" },
    ],
  },
  {
    title: "CEO",
    status: "v0.3.0",
    viz: "ceo",
    area: ["agents", "tooling"],
    blurb: "CEO is a terminal orchestrator for teams of headless AI agents. Users hire agents with role briefs, then set a mission, autonomy levels and per-agent budgets before delegating work. Each agent operates as a claude -p or codex exec process, with an optional dedicated git worktree for isolated agents. Autonomy is enforced at the process level through a tool allowlist on Claude and a filesystem sandbox on Codex, with every refused command recorded as a denial. Every delegation and every dollar goes into a hash-chained ledger reviewed by auditor and postmortem agents, with each cost marked settled or estimated from list prices.",
    stack: ["TypeScript", "CLI", "MCP", "claude -p / codex exec", "git worktrees", "tmux"],
    links: [],
  },
  {
    title: "OrgSim-Gym",
    status: "In development",
    viz: "orgsim",
    area: ["agents", "simulation", "evals"],
    blurb: "OrgSim-Gym is a long-horizon, multi-actor workplace environment for training and evaluating LLM agents. The agent coordinates a cross-team project with 4 to 8 simulated coworkers whose personalities drive hidden dynamics such as inflated progress reports, burnout under stress, and shifting trust. Separate honesty and wellbeing terms in the decomposed reward turn reward-hacking experiments into a configuration change.",
    stack: ["Python", "RL environment", "Anthropic / OpenAI"],
    links: [],
  },
  {
    title: "Teaching Robots to Walk from Smart-Shoe Data",
    status: "In development",
    viz: "robots",
    area: ["robotics"],
    blurb: "This project is a collaboration with a stealth-stage smart-shoe company. Human gait is reconstructed from bilateral foot IMUs and used as a motion prior for reinforcement learning in a simulated humanoid. Trained on free-living data from 8 verified wearers over 137 wearer-days, the policy stays upright about as well as a policy trained on a motion-capture benchmark (496.1 vs 499.1 of 500 steps, 3 seeds). Steadiness saturates after roughly three hours of a single wearer\u2019s data. Replayed recordings train as well as genuine recordings, so the case for genuine data rests on data rights. Motion capture still yields a faster, more efficient gait. All results are in simulation.",
    stack: ["PPO", "MuJoCo MJX", "JAX", "IMU sensing", "Modal"],
    links: [],
  },
  {
    title: "ARENA",
    status: "Live",
    viz: "arena",
    area: ["agents", "platform"],
    blurb: "ARENA is a production classroom platform where student teams delegate work to Claude agents and submit the results to a simulated buyer panel. A 60-minute stress test with 35 teams and 100 concurrent students kept server-action p95 latency at 237 ms.",
    stack: ["Next.js", "Supabase", "Inngest", "Claude", "GCP"],
    links: [],
  },
];

const RESEARCH = [
  { title: "Benchmarking Psychological Dynamics in Generative Agents",
    authors: ["Vaid, S.S.", "Whillans, A.V."],
    status: "Accepted, AI for Measurement Science Workshop @ COLM 2026",
    topics: ["evals", "agents"],
    links: [{ label: "arXiv", href: "https://arxiv.org/abs/2610.04246" }, { label: "Code", href: "https://github.com/sumervaid/PsyDyBench" }] },
  { title: "Toward an AI-Powered Computational Testbed for Workforce Policy",
    authors: ["Vaid, S.S.", "Whillans, A.V."],
    status: "Under review, Harvard Data Science Review",
    topics: ["agents", "simulation"],
    links: [{ label: "arXiv", href: "https://arxiv.org/abs/2605.19064" }] },
  { title: "Digital Fragmentation and Generative AI Use Across 107 Million Application Events",
    authors: ["Vaid, S.S.", "Whillans, A.V."],
    status: "Preprint, in submission",
    topics: ["AI adoption", "telemetry"],
    links: [{ label: "arXiv (v1)", href: "https://arxiv.org/abs/2607.06681" }] },
  { title: "Psychology at Token Resolution: A Measurement-Theoretic Framework for Mechanistic Interpretability",
    authors: ["Vaid, S.S."],
    status: "In preparation",
    topics: ["interpretability"],
    links: [] },
  { title: "Activity Variety Predicts Subjective Wellbeing Better Than Personality–Activity Fit Across Seven Countries",
    authors: ["Vaid, S.S.", "Matic, A.", "Whillans, A.V."],
    status: "In preparation",
    topics: ["wellbeing"],
    links: [] },
  { title: "The Daily Dynamics of Informal Interactions and Psychological Safety in Hybrid Work",
    authors: ["Vaid, S.S.", "Huang, C.G.", "Wiwad, D.", "Sadeq-Keyes, R.", "Puente, L.", "Massimi, M.", "Whillans, A.V."],
    status: "In preparation",
    topics: ["future of work"],
    links: [] },
];

const PUBS = [
  { title: "EmotionVibe: Human Emotion Recognition Through Footstep-Induced Floor Vibrations",
    authors: ["Wu, Y.", "Dong, Y.", "Vaid, S.S.", "Harari, G.M.", "Noh, H.Y."],
    venue: "IEEE Transactions on Affective Computing",
    year: 2026,
    links: [],
    topics: ["machine learning", "sensing"] },
  { title: "Where Senior Leaders Are Struggling with AI Adoption, According to Research",
    authors: ["Croft, J.", "Vaid, S.S.", "Cheng, L.", "Whillans, A.V."],
    venue: "Harvard Business Review",
    year: 2026,
    links: [{ label: "HBR", href: "https://store.hbr.org/product/where-senior-leaders-are-struggling-with-ai-adoption-according-to-research/H0934Z" }],
    topics: ["artificial intelligence", "AI adoption"] },
  { title: "Digital Media and Personalized Persuasion",
    authors: ["Vaid, S.S.", "Harari, G.M.", "Matz, S.C."],
    venue: "The Handbook of Personalized Persuasion: Theory and Application",
    year: 2025,
    links: [{ label: "Chapter", href: "#" }],
    topics: ["persuasion", "digital media", "personalization"] },
  { title: "Modeling Social Media Habits Using Transformers and LSTM Neural Networks",
    authors: ["Peters, H.", "Chi, Y.", "Matz, S.", "Bayer, J.", "Vaid, S.S.", "Harari, G.M."],
    venue: "Computers in Human Behavior",
    year: 2024,
    links: [{ label: "Paper", href: "https://arxiv.org/abs/2404.16066" }],
    topics: ["deep learning", "computational methods", "social media use"] },
  { title: "Contextualizing Meaningful Social Interactions and Momentary Well-Being in Everyday Life",
    authors: ["Roshanaei, M.", "Vaid, S.S.", "Courtney, A.", "Soh, S.", "Zaki, J.", "Harari, G.M."],
    venue: "Social Psychological and Personality Science",
    year: 2023,
    links: [{ label: "Paper", href: "https://doi.org/10.31234/osf.io/gu4pv" }],
    topics: ["psychological fit", "computational methods"] },
  { title: "Variation in Social Media Sensitivity Across People and Contexts",
    authors: ["Vaid, S.S.", "Kroencke, L.", "Roshanaei, M.", "Talaifar, S.", "Hancock, J.", "Back, M.", "Gosling, S.D.", "Ram, N.", "Harari, G.M."] ,
    venue: "Scientific Reports",
    year: 2023,
    links: [{ label: "Paper", href: "https://doi.org/10.31234/osf.io/yfp45" }],
    topics: ["psychological fit", "computational methods", "social media use"] },
  { title: "The Potential of Generative AI for Personalized Persuasion at Scale",
    authors: ["Matz, S.C.", "Teeny, J.", "Vaid, S.S.", "Harari, G.M.", "Cerf, M."],
    venue: "Scientific Reports",
    year: 2023,
    links: [{ label: "Paper", href: "https://doi.org/10.31234/osf.io/rn97c" }],
    topics: ["psychological fit", "artificial intelligence"] },
  { title: "Paradigm shifts in digital personalization",
    authors: ["Vaid, S.S."],
    venue: "Nature Reviews Psychology",
    year: 2023,
    links: [{ label: "Editorial", href: "https://doi.org/10.1038/s44159-023-00202-y" }],
    topics: ["editorial", "personalization", "artificial intelligence"] },
  { title: "Situating Smartphones in Everyday Life: Personality Traits and Situational Context Shape Momentary Smartphone Use",
    authors: ["Roehrick, K.*", "Vaid, S.S.*", "Harari, G.M."],
    venue: "Journal of Personality and Social Psychology",
    year: 2023,
    links: [{ label: "Paper", href: "https://doi.org/10.1037/pspp0000478" }],
    topics: ["computational methods", "psychological fit"] },
  { title: "Who uses what and how often?: Personality predictors of multiplatform social media use among young adults",
    authors: ["Vaid, S.S.", "Harari, G.M."],
    venue: "Journal of Research in Personality",
    year: 2021,
    links: [{ label: "Paper", href: "https://doi.org/10.1016/j.jrp.2020.104005" }],
    topics: ["computational methods", "social media use"] },
  { title: "Ubiquitous Computing Technologies and Machine Learning Techniques for Investigating Person-Environment Interactions",
    authors: ["Vaid, S.S.", "Abdullah, S.", "Thomaz, E.", "Harari, G.M."],
    venue: "Emerging Approaches to Measuring and Modeling the Person and Situation",
    year: 2021,
    links: [{ label: "Chapter", href: "#" }],
    topics: ["ubiquitous computing", "machine learning", "person environment fit"] },
  { title: "Personality Sensing for Theory Development and Assessment in the Digital Age",
    authors: ["Harari, G.M.", "Vaid, S.S.", "Müller, S.R.", "Stachl, C.", "Marrero, Z.", "Schoedel, R.", "Bühner, M.", "Gosling, S.D."] ,
    venue: "European Journal of Personality",
    year: 2020,
    links: [{ label: "Paper", href: "https://doi.org/10.1002/per.2273" }],
    topics: ["computational methods", "personality prediction"] },
  { title: "Personality Research and Assessment in the Era of Machine Learning",
    authors: ["Stachl, C.", "Pargent, F.", "Hilbert, S.", "Harari, G.M.", "Schoedel, R.", "Vaid, S.S.", "Gosling, S.D.", "Bühner, M."] ,
    venue: "European Journal of Personality",
    year: 2020,
    links: [{ label: "Paper", href: "https://doi.org/10.1002/per.2257" }],
    topics: ["machine learning", "personality prediction"] },
  { title: "Smartphones in Personal Informatics: A Framework for Self-Tracking Research with Mobile Sensing",
    authors: ["Vaid, S.S.", "Harari, G.M."],
    venue: "Digital Phenotyping and Mobile Sensing",
    year: 2019,
    links: [{ label: "Chapter", href: "https://doi.org/10.1007/978-3-030-31620-4_5" }],
    topics: ["mobile sensing", "digital phenotyping", "self tracking"] },
  { title: "Modeling Personality vs. Modeling Personalidad: In-the-wild Mobile Data Analysis in Five Countries Suggests Cultural Impact on Personality Models",
    authors: ["Khwaja, M.", "Vaid, S.S.", "Zannone, S.", "Harari, G.M.", "Faisal, A.A.", "Matic, A."] ,
    venue: "Proceedings of the ACM on Interactive, Mobile, Wearable and Ubiquitous Technologies",
    year: 2019,
    links: [{ label: "Paper", href: "https://dl.acm.org/doi/10.1145/3351246" }],
    topics: ["computational methods", "machine learning", "personality prediction", "digital logs"] },
];

const TALKS = [
  { title: "Leveraging LLM-Powered Generative Agents to Simulate Employee Responses to Organizational Wellbeing Interventions", meta: "SPSP Annual Convention, Philadelphia (scheduled), February 2027" },
  { title: "Predicting the Future of Work", meta: "Digital Data Design Institute, Harvard Business School, December 2025" },
  { title: "Generative Agents in the Workplace", meta: "Experimental Methods in Behavioral Research (doctoral course), Harvard Business School, November 2025" },
  { title: "The Dynamics of Daily Informal Conversations and Psychological Safety", meta: "Organizational Behavior Lab, London (Remote), February 2025" },
  { title: "Methodological Guidance for Studying Digital Wellbeing in Everyday Life", meta: "IRiSS Predoctoral Research Fellowship, Stanford, March 2024" },
  { title: "Situating Social Media Contexts in Everyday Contexts", meta: "Center for Open Science & UVA Thriving Youth Webinar, March 2024" },
  { title: "Social Media Sensitivity", meta: "Harvard Business School, Goldenberg Lab & NERD Lab, March 2024" },
];

const AWARDS = [
  { title: "Digital Data Design Institute Associates Award (Co-PI)", meta: "Harvard Business School, 2025, $50,000" },
  { title: "Best Dissertation Award", meta: "Psychology of Technology Institute, 2024, $1,000" },
  { title: "HAI Google Cloud Credit Award", meta: "Stanford University, 2022–2023, $98,000" },
  { title: "Research Assistantship at Journal of Online Trust & Safety", meta: "Stanford Internet Observatory, 2023, $76,000" },
  { title: "King Center for Global Development Graduate Research Funding", meta: "Stanford University, 2022, $8,000" },
  { title: "Computational Social Science Fellowship", meta: "Institute for Research in Social Sciences, Stanford University, 2022, $5,000" },
  { title: "Ric Weiland Graduate Fellowship", meta: "Stanford University, 2021, $5,000" },
  { title: "McCoy Center for Ethics Fellowship", meta: "Stanford University, 2021, $3,000" },
  { title: "Association for Research in Personality Emerging Scholar & Diversity Award", meta: "Stanford University, 2020, $250" },
  { title: "Stanford Center on Democracy Research Grant", meta: "Stanford University, 2020, $1,500" },
];

// Layout modeled on saffronhuang.com: bio column, featured-work grid, link column,
// then full-width About and two-column lists. Dark theme only.
const FUTURA = "font-['Futura','Futura_PT','Century_Gothic',Helvetica,Arial,sans-serif] font-medium";
const HEAD = `${FUTURA} text-[#f0f0f0]`;
const TAB = `${FUTURA} text-[15px] pb-2 -mb-px border-b-2 transition-colors hover:text-white`;
const TABS = [['builds', 'Open source builds'], ['writing', 'Academic writing']];
const LINK = "text-[#a3a3a3] underline hover:text-white";
const firstHref = links => (links || []).map(l => l.href).find(h => h && h !== "#");

const A = ({ href, children }) => href
  ? <a href={href} target={href.startsWith("mailto:") ? undefined : "_blank"} rel="noreferrer" className={LINK}>{children}</a>
  : <span>{children}</span>;

const ProjectCard = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#2a2d33] bg-[#111317] flex flex-col min-w-0 transition-colors hover:border-[#737373]">
      {p.viz && <CardViz kind={p.viz} flat/>}
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="p-4 flex-1 flex flex-col text-left">
        <h3 className={`${HEAD} font-normal text-sm leading-snug mb-1`}>{p.title}</h3>
        <p className="text-xs text-[#a3a3a3] font-normal mb-3">{p.status}</p>
        <p className={`text-xs leading-[1.4] text-[#a3a3a3] font-light ${open ? '' : 'line-clamp-4'}`}>{p.blurb}</p>
      </button>
      {p.links.length > 0 && (
        <div className="px-4 pb-4 -mt-1 flex gap-4 text-xs">
          {p.links.map(l => <A key={l.href} href={l.href}>{l.label}</A>)}
        </div>
      )}
    </div>
  );
};

export default function Portfolio() {
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.classList.add('dark');
  }, []);
  const [tab, setTab] = useState(() => (typeof window !== 'undefined' && window.location.hash === '#writing' ? 'writing' : 'builds'));
  const pickTab = id => {
    setTab(id);
    window.history.replaceState(null, '', id === 'writing' ? '#writing' : window.location.pathname);
  };

  return (
    <div className="min-h-screen bg-[#0b0d10] text-[#d4d4d4] font-light text-sm leading-[1.4] font-['Helvetica',Helvetica,Arial,sans-serif] selection:bg-[#4a2c33] selection:text-white">
      <div id="about" className="grid lg:grid-cols-[1.2fr_3fr_1fr] gap-6 lg:gap-8 max-w-[1600px] mx-auto px-4 sm:px-8 pt-5 sm:pt-8 pb-8 items-start">
        {/* Bio column */}
        <aside className="lg:pr-4 max-w-[600px] w-full mx-auto lg:mx-0">
          <h1 className={`${HEAD} text-2xl mt-4 mb-1 text-center lg:text-left`}>{PROFILE.name}</h1>
          <p className="text-[#a3a3a3] mb-4 text-center lg:text-left">{PROFILE.title}</p>
          <img
            src="/vaid_portrait_2026.jpg"
            alt="Sumer S. Vaid"
            className="block w-[200px] h-auto mx-auto lg:mx-0 mb-8 grayscale hover:grayscale-0 transition duration-500 ease-out"
          />
          <p className="mb-4">{PROFILE.bio[0]}</p>
          <div className="grid grid-cols-2 divide-x divide-[#2a2d33] border-y border-[#2a2d33] py-3 mb-4 text-center">
            {[['Stanford University', 'Ph.D., M.A.'], ['University of Chicago', 'B.A., M.A.']].map(([school, degrees]) => (
              <div key={school} className="px-2 whitespace-nowrap text-[13px]">
                <p className="font-normal">{school}</p>
                <p className="text-[#a3a3a3]">{degrees}</p>
              </div>
            ))}
          </div>
          <a href="#more" className="inline-block border border-[#2a2d33] text-[#a3a3a3] px-4 py-2 mt-4 transition-colors hover:border-[#737373] hover:text-white">Full bio</a>
        </aside>

        {/* Featured work */}
        <main className="relative min-w-0">
          <div className="lg:px-6 lg:border-x border-[#2a2d33]">
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 border-b border-[#2a2d33] mt-3 mb-6">
              <div role="tablist" aria-label="Work" className="flex gap-6">
                {TABS.map(([id, label]) => (
                  <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => pickTab(id)} className={`${TAB} ${tab === id ? 'border-white text-white' : 'border-transparent text-[#a3a3a3]'}`}>{label}</button>
                ))}
              </div>
              <a href="/predict/" target="_blank" rel="noreferrer" className={`${TAB} border-transparent text-[#a3a3a3]`}>AI token prediction ↗</a>
            </div>
            {tab === 'builds' ? (
              <div role="tabpanel" className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
                {SYSTEMS.map(p => <ProjectCard key={p.title} p={p}/>)}
              </div>
            ) : (
              <div role="tabpanel" className="max-w-[720px] mx-auto pb-4">
                <h2 className={`${HEAD} text-2xl mb-4`}>Research in Progress</h2>
                <ul className="list-disc pl-5 space-y-2 mb-10">
                  {RESEARCH.map(r => (
                    <li key={r.title}>
                      <A href={firstHref(r.links)}>{r.title}</A> - {r.authors.join(", ")} {r.status}.
                      {r.links.length > 1 && <> {r.links.slice(1).map(l => <span key={l.href}> <A href={l.href}>{l.label}</A></span>)}</>}
                    </li>
                  ))}
                </ul>
                <h2 className={`${HEAD} text-2xl mb-4`}>Publications</h2>
                <ul className="list-disc pl-5 space-y-2">
                  {PUBS.map(p => (
                    <li key={p.title}>
                      <A href={firstHref(p.links)}>{p.title}</A> - {p.authors.join(", ")} ({p.year}). <em>{p.venue}</em>.
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </main>

        {/* Link column */}
        <aside className="lg:pl-4 max-w-[600px] w-full mx-auto lg:mx-0">
          <h2 className={`${HEAD} text-base mt-4 mb-4`}>Links</h2>
          <ul className="list-disc pl-5 mb-6">
            <li><A href={PROFILE.links.github}>GitHub</A></li>
            <li><A href={PROFILE.links.scholar}>Google Scholar</A></li>
            <li><A href={PROFILE.links.linkedin}>LinkedIn</A></li>
            <li><A href={`mailto:${PROFILE.email}`}>Email</A></li>
            <li><A href="/predict/">AI token prediction</A></li>
          </ul>
          <h2 className={`${HEAD} text-base mb-4`}>Methods</h2>
          <p className="text-[#a3a3a3]">{PROFILE.skills.join(" · ")}</p>
        </aside>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 pb-12">
        <section id="more" className="my-12 max-w-[600px] lg:max-w-none mx-auto">
          <h2 className={`${HEAD} text-2xl mb-4`}>About</h2>
          {PROFILE.bio.map((para, i) => <p key={i} className="mb-3">{para}</p>)}
        </section>

        <section id="talks" className="my-12 grid md:grid-cols-2 gap-8 md:gap-12">
          <div>
            <h2 className={`${HEAD} text-2xl mb-4`}>Talks</h2>
            <ul className="list-disc pl-5 space-y-2">
              {TALKS.map(t => <li key={t.title}><span className="font-normal">{t.title}</span> - {t.meta}</li>)}
            </ul>
          </div>
          <div>
            <h2 className={`${HEAD} text-2xl mb-4`}>Awards</h2>
            <ul className="list-disc pl-5 space-y-2">
              {AWARDS.map(a => <li key={a.title}><span className="font-normal">{a.title}</span> - {a.meta}</li>)}
            </ul>
          </div>
        </section>

        <footer id="contact" className="border-t border-[#2a2d33] pt-6 text-[#a3a3a3]">
          For collaboration or hiring inquiries, please email <A href={`mailto:${PROFILE.email}`}>{PROFILE.email}</A>.
        </footer>
      </div>
    </div>
  );
}
